/**
 * Compare Creators (OutlierOS /compare replica, docs/legacy-ui/01 §5), placement kept: header with chips and
 * "+ Add Creator" → profile cards → "Relative Benchmark Breakdown" (normalised bars) → "Top Wise Score Hooks & Reels
 * Side-by-Side" (OutlierOS named it after outliers). Added below (DASHBOARD_PARITY §3.2): Wise Score over time, overlaid, and "when it works" per
 * creator. Up to 5 creators (was 4). Plan feature `compare`, enforced by the API. Selection lives in the URL.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, ExternalLink, Lock, Plus, Search, X } from 'lucide-react';
import { COMPARE_MAX_CREATORS } from '@vw/core/src/limits';
import { heatmap, parseWindow, REPORT_LATEST_CHOICES, windowKey, type ReportKpis, type ReportReel } from '@vw/core/src/report';
import { Button, EmptyState, ErrorState, HeaderCard, Pill, Select, Spinner, cn, formatWiseScore, toast, wiseScoreLabel, type TierThresholds } from '@vw/ui';
import { api, ApiError, type CreatorCard, type SavedCreator } from '../api';
import { CreatorAvatar } from '../components/CreatorAvatar';
import { Heatmap } from '../components/Charts';
import { compact, date, igReel, pct } from '../format';
import { Link, navigate, useLocation } from '../router';
import { useSession } from '../session';
import { BROWSER_ZONE } from '../components/workbench/ReportTab';

interface Entry { creator: CreatorCard; kpis: ReportKpis; reels: ReportReel[]; tiers: TierThresholds; unavailable?: undefined }
type Slot = Entry | { igPk: string; unavailable: true };
const COLORS = ['#f97316', '#6366f1', '#10b981', '#ec4899', '#0ea5e9'] as const; // one per creator (COMPARE_MAX_CREATORS)

export function Compare() {
  const { me, library } = useSession();
  const { query } = useLocation();
  const allowed = !!me.plan.limits?.compare || me.user.role === 'admin';
  const saved = useMemo(() => (library ?? []).filter((x): x is SavedCreator => !x.unavailable), [library]);
  const fromUrl = [...new Set((query.get('pks') ?? '').split(',').filter(Boolean))].slice(0, COMPARE_MAX_CREATORS);
  // First visit: the two highest peak Wise Scores in the library (OutlierOS opened with the first two tracked creators).
  const pks = fromUrl.length ? fromUrl : [...saved].sort((a, b) => (b.peakOutlier ?? 0) - (a.peakOutlier ?? 0)).slice(0, 2).map((c) => c.igPk);
  const win = parseWindow(query.get('window')) ?? { kind: 'latest' as const, n: 100 };
  const wKey = windowKey(win);
  const [data, setData] = useState<Slot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = pks.join(',');

  const setUrl = (next: string[], w = wKey) => navigate(`/compare?pks=${next.join(',')}&window=${w}`, { replace: true });

  useEffect(() => {
    if (!allowed || !key) return;
    let live = true;
    setError(null);
    setData(null);
    api<{ creators: Slot[] }>(`/api/compare?pks=${key}&window=${wKey}`)
      .then((r) => live && setData(r.creators), (e) => live && setError(e instanceof ApiError ? e.message : 'Could not load the comparison'));
    return () => { live = false; };
  }, [allowed, key, wKey, attempt]);

  if (!allowed) {
    return (
      <EmptyState icon={<Lock className="h-7 w-7" />} title="Compare Creators is on the paid plans"
        actions={<Link to="/billing" className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground">See plans</Link>}>
        Put up to {COMPARE_MAX_CREATORS} creators side by side: peak Wise Score, usual views, followers and their best hooks.
      </EmptyState>
    );
  }
  if (library && !saved.length) {
    return <EmptyState icon={<ArrowUpDown className="h-7 w-7" />} title="Nothing to compare yet" actions={<Link to="/explore" className="text-sm font-semibold text-primary">Browse the Creator Library →</Link>}>Scan or save at least one creator, then compare them here.</EmptyState>;
  }

  const add = (pk: string) => {
    if (pks.length >= COMPARE_MAX_CREATORS) return toast.info(`You can compare up to ${COMPARE_MAX_CREATORS} creators`);
    setUrl([...pks, pk]);
  };
  const remove = (pk: string) => {
    if (pks.length <= 1) return toast.info('Keep at least one creator');
    setUrl(pks.filter((p) => p !== pk));
  };
  const entries = (data ?? []).filter((x): x is Entry => !x.unavailable);
  const nameOf = (pk: string) => {
    const s = (data ?? []).find((x) => (x.unavailable ? x.igPk : x.creator.igPk) === pk);
    if (s && !s.unavailable) return s.creator.handle ?? pk;
    return saved.find((c) => c.igPk === pk)?.handle ?? 'Unavailable';
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <HeaderCard>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"><ArrowUpDown className="h-5 w-5" /></span>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Compare Creators</h1>
              <p className="mt-1 text-sm text-muted-foreground">Head-to-head viral benchmarks: who breaks out, how often, and with which hooks.</p>
            </div>
          </div>
          <Select aria-label="Reels to compare" value={wKey} onChange={(e) => setUrl(pks, e.target.value)}>
            {REPORT_LATEST_CHOICES.map((n) => <option key={n} value={`latest:${n}`}>Latest {n} reels</option>)}
            {[7, 30, 90].map((d) => <option key={d} value={`days:${d}`}>Last {d} days</option>)}
          </Select>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {pks.map((pk, i) => (
            <span key={pk} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background py-1 pl-3 pr-1 text-xs font-semibold">
              <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i] }} />@{nameOf(pk)}
              <button aria-label={`Remove @${nameOf(pk)}`} onClick={() => remove(pk)} className="rounded-full p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
          <AddCreator saved={saved} chosen={pks} onPick={add} full={pks.length >= COMPARE_MAX_CREATORS} />
        </div>
      </HeaderCard>

      {error ? <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} /> : !data ? <Spinner label="Comparing creators…" /> : (
        <>
          <section className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
            {data.map((s, i) => s.unavailable ? (
              <div key={s.igPk} className="flex items-center justify-center rounded-3xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">This creator isn't available any more.</div>
            ) : <ProfileCard key={s.creator.igPk} e={s} color={COLORS[i]!} />)}
          </section>

          {entries.length > 0 && (
            <>
              <section className="rounded-3xl border border-border bg-card p-5 shadow-xs sm:p-6">
                <h2 className="mb-4 font-extrabold">Relative Benchmark Breakdown</h2>
                <div className="grid gap-6 md:grid-cols-3">
                  <Bars title="Peak Wise Score" grad="from-amber-400 to-orange-500" rows={entries.map((e) => ({ e, v: peak(e), label: peak(e) ? formatWiseScore(peak(e)!) : '–' }))} />
                  <Bars title="Usual views" grad="from-blue-500 to-indigo-500" rows={entries.map((e) => ({ e, v: e.kpis.baselineViews, label: compact(e.kpis.baselineViews) }))} />
                  <Bars title="Followers" grad="from-emerald-500 to-teal-500" rows={entries.map((e) => ({ e, v: e.creator.followers, label: compact(e.creator.followers) }))} />
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="font-extrabold">Top Wise Score Hooks & Reels Side-by-Side</h2>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
                  {entries.map((e) => <TopReels key={e.creator.igPk} e={e} />)}
                </div>
              </section>

              <section className="rounded-3xl border border-border bg-card p-5 shadow-xs sm:p-6">
                <h2 className="font-extrabold">Wise Score over time</h2>
                <p className="mb-3 text-xs text-muted-foreground">Each dot is a reel: how many times its creator's usual views it got.</p>
                <Overlay entries={entries} colorOf={(pk) => COLORS[pks.indexOf(pk)] ?? COLORS[0]!} />
              </section>

              <section className="space-y-3">
                <h2 className="font-extrabold">When it works <span className="text-xs font-normal text-muted-foreground">· median Wise Score by day and hour ({BROWSER_ZONE})</span></h2>
                <div className="grid gap-4 lg:grid-cols-2">
                  {entries.map((e) => (
                    <div key={e.creator.igPk} className="rounded-3xl border border-border bg-card p-4 shadow-xs">
                      <div className="mb-2 text-sm font-bold">@{e.creator.handle}</div>
                      <Heatmap cells={heatmap(e.reels, BROWSER_ZONE, 'works')} mode="works" />
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}

const peak = (e: Entry) => e.reels.reduce<number | null>((m, r) => (r.outlierScore !== null && (m === null || r.outlierScore > m) ? r.outlierScore : m), null);

function ProfileCard({ e, color }: { e: Entry; color: string }) {
  const c = e.creator;
  const scored = e.reels.filter((r) => r.outlierScore !== null);
  const above = scored.length ? scored.filter((r) => r.outlierScore! >= e.tiers.above).length / scored.length : null;
  const p = peak(e);
  return (
    <div className="space-y-4 rounded-3xl border border-border bg-card p-5 shadow-xs" style={{ borderTopColor: color, borderTopWidth: 3 }}>
      <div className="flex items-center gap-3">
        <CreatorAvatar url={c.avatarUrl} name={c.handle ?? ''} fullName={c.fullName} size={44} />
        <div className="min-w-0">
          <div className="truncate font-bold">{c.fullName ?? c.handle}</div>
          <Link to={`/c/${c.handle}`} className="text-xs text-muted-foreground hover:text-primary">@{c.handle}</Link>
        </div>
      </div>
      <Pill tone="primary">{wiseScoreLabel(p, e.tiers)} Peak Wise Score</Pill>
      <dl className="grid grid-cols-2 gap-2 text-center">
        <Stat k="Usual views" v={compact(e.kpis.baselineViews)} />
        <Stat k="Reels analysed" v={String(e.kpis.reels)} />
        <Stat k={`Reels ≥ ${formatWiseScore(e.tiers.above)}`} v={pct(above, 0)} />
        <Stat k="Followers" v={compact(c.followers)} />
      </dl>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return <div className="rounded-xl bg-secondary/60 p-2.5"><dd className="text-lg font-black tabular-nums">{v}</dd><dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</dt></div>;
}

function Bars({ title, grad, rows }: { title: string; grad: string; rows: Array<{ e: Entry; v: number | null; label: string }> }) {
  const max = Math.max(1, ...rows.map((r) => r.v ?? 0));
  return (
    <div className="space-y-2.5">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {rows.map(({ e, v, label }) => (
        <div key={e.creator.igPk} className="space-y-1">
          <div className="flex justify-between text-xs"><span className="truncate font-semibold">@{e.creator.handle}</span><span className="tabular-nums text-muted-foreground">{label}</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary"><div className={cn('h-full rounded-full bg-gradient-to-r', grad)} style={{ width: `${((v ?? 0) / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function TopReels({ e }: { e: Entry }) {
  const top = e.reels.filter((r) => r.outlierScore !== null).sort((a, b) => b.outlierScore! - a.outlierScore!).slice(0, 3);
  return (
    <div className="space-y-3 rounded-3xl border border-border bg-card p-4 shadow-xs">
      <div className="text-sm font-bold">@{e.creator.handle}</div>
      {!top.length && <p className="text-xs text-muted-foreground">No scored reels in this window.</p>}
      {top.map((r, i) => {
        const hook = r.hook && r.hook.trim() !== (r.caption ?? '').trim() ? r.hook : null;
        return (
          <div key={r.pk} className="space-y-2 rounded-2xl border border-border p-3">
            <div className="flex items-center justify-between text-xs font-bold"><span>#{i + 1}</span><span className="text-amber-600 dark:text-amber-400">{wiseScoreLabel(r.outlierScore, e.tiers)}</span></div>
            <div className="flex gap-3">
              {r.thumbUrl ? <img src={r.thumbUrl} alt="" referrerPolicy="no-referrer" loading="lazy" className="aspect-[9/16] w-16 shrink-0 rounded-lg object-cover" />
                : <span className="aspect-[9/16] w-16 shrink-0 rounded-lg bg-secondary" />}
              <div className="min-w-0 space-y-1 text-xs">
                <div className="font-semibold tabular-nums">{compact(r.views)} views</div>
                <a href={igReel(r.shortcode)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary">Watch Reel<ExternalLink className="h-3 w-3" /></a>
              </div>
            </div>
            <div className="rounded-xl bg-secondary/60 p-2.5">
              <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Spoken hook (first 3–5 s)</div>
              <p lang={r.hookScript === 'devanagari' ? 'hi' : undefined} className={cn('mt-1 text-xs', hook ? 'font-semibold italic' : 'text-muted-foreground')}>{hook ? `“${hook}”` : 'Not transcribed yet'}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** All creators on one time axis; y = Wise Score on a log scale, so different audience sizes compare fairly. */
function Overlay({ entries, colorOf }: { entries: Entry[]; colorOf: (pk: string) => string }) {
  const pts = entries.flatMap((e) => e.reels.filter((r) => r.postedAt !== null && r.outlierScore !== null && r.outlierScore > 0)
    .map((r) => ({ pk: e.creator.igPk, handle: e.creator.handle, x: r.postedAt!, y: r.outlierScore!, shortcode: r.shortcode })));
  if (!pts.length) return <p className="text-sm text-muted-foreground">No scored reels in this window.</p>;
  const W = 720, H = 260, L = 44, R = 12, T = 10, B = 26;
  const xs = pts.map((p) => p.x);
  const x0 = Math.min(...xs), x1 = Math.max(...xs, x0 + 1);
  const ly = (v: number) => Math.log10(Math.max(0.05, v));
  const y0 = ly(Math.min(0.1, ...pts.map((p) => p.y))), y1 = ly(Math.max(10, ...pts.map((p) => p.y)));
  const px = (x: number) => L + ((x - x0) / (x1 - x0)) * (W - L - R);
  const py = (v: number) => T + (1 - (ly(v) - y0) / (y1 - y0)) * (H - T - B);
  const ticks = [0.1, 1, 3, 10, 100].filter((v) => ly(v) >= y0 && ly(v) <= y1);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Wise Score of each reel over time, by creator">
      {ticks.map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={py(v)} y2={py(v)} stroke="currentColor" strokeOpacity={v === 1 ? 0.35 : 0.1} strokeDasharray={v === 1 ? '4 4' : undefined} />
          <text x={L - 6} y={py(v) + 3} textAnchor="end" fontSize="10" fill="currentColor" opacity={0.6}>{v}×</text>
        </g>
      ))}
      {pts.map((p) => (
        <a key={`${p.pk}-${p.shortcode}`} href={igReel(p.shortcode)} target="_blank" rel="noreferrer">
          <circle cx={px(p.x)} cy={py(p.y)} r={3.5} fill={colorOf(p.pk)} fillOpacity={0.75}><title>@{p.handle} · {formatWiseScore(p.y)} · {date(p.x)}</title></circle>
        </a>
      ))}
    </svg>
  );
}

function AddCreator({ saved, chosen, onPick, full }: { saved: SavedCreator[]; chosen: string[]; onPick: (pk: string) => void; full: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [open]);
  const s = q.trim().toLowerCase().replace(/^@/, '');
  const list = saved.filter((c) => !s || (c.handle ?? '').toLowerCase().includes(s) || (c.fullName ?? '').toLowerCase().includes(s)).slice(0, 50);
  return (
    <div className="relative" ref={ref}>
      <Button size="sm" variant="secondary" onClick={() => (full ? toast.info(`You can compare up to ${COMPARE_MAX_CREATORS} creators`) : setOpen(!open))} aria-expanded={open}>
        <Plus className="h-3.5 w-3.5" />Add Creator
      </Button>
      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-40 w-72 animate-zoom-in rounded-2xl border border-border bg-card p-2 shadow-xl">
          <label className="flex items-center gap-2 rounded-xl border border-border px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your creators" className="w-full bg-transparent text-sm outline-none" />
          </label>
          <ul className="mt-1.5 max-h-72 overflow-y-auto">
            {list.map((c) => {
              const taken = chosen.includes(c.igPk);
              return (
                <li key={c.igPk}>
                  <button disabled={taken} onClick={() => { onPick(c.igPk); setOpen(false); setQ(''); }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left hover:bg-secondary disabled:opacity-40">
                    <CreatorAvatar url={c.avatarUrl} name={c.handle ?? ''} fullName={c.fullName} size={28} />
                    <span className="min-w-0 flex-1"><span className="block truncate text-xs font-bold">{c.fullName ?? c.handle}</span><span className="block truncate text-[11px] text-muted-foreground">@{c.handle}</span></span>
                    <span className="font-mono text-[11px] text-amber-600 dark:text-amber-400">{c.peakOutlier ? formatWiseScore(c.peakOutlier) : '–'}</span>
                  </button>
                </li>
              );
            })}
            {!list.length && <li className="px-2 py-3 text-center text-xs text-muted-foreground">No creators match.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
